import i18next, { t } from 'i18next'
import React, { ReactNode, RefObject } from 'react'
import { Link, navigate } from 'widgets-for-react'
import { getPathWithSearchParam, getPathWithUpdatedSearchParams } from '../utils/getPathWithUpdatedSearchParams'
import { SVGIcon } from '../utils/svg'

export const Paginator = ({
  currentPage,
  totalPages,
  scrollToRef,
}: {
  currentPage: number
  totalPages: number
  scrollToRef?: RefObject<HTMLElement>
}) => {
  const pageNumbersBefore = []
  for (let page = Math.max(1, currentPage - 4); page < currentPage; page++)
    pageNumbersBefore.push(<PaginatorNumber page={page} key={page} scrollToRef={scrollToRef} />)

  const pageNumbersAfter = []
  for (let page = currentPage + 1; page <= Math.min(totalPages, currentPage + 4); page++)
    pageNumbersAfter.push(<PaginatorNumber page={page} key={page} scrollToRef={scrollToRef} />)

  if (currentPage > totalPages) {
    // This situation can happen if the user manually types the number into the browser
    navigate(getPathWithSearchParam('page', totalPages.toString()))
    return <></>
  }

  return (
    <nav className="pager ls-spacing" aria-label={t('Pager:A11YLabel')}>
      <ul className="pager__list text--sm">
        <li className="pager__item pager__item--prev">
          <PaginatorLink
            className="pager__prev"
            page={currentPage - 1}
            scrollToRef={scrollToRef}
            isDisabled={currentPage <= 1}
          >
            <SVGIcon id='icon-arrow-left' size={20} className='pager__icon' />
            <PreviousLabel />
          </PaginatorLink>
        </li>

        <li className="pager__item pager__item--pages">
          <ol className="pager__pages">
            {currentPage > 5 && (
              <>
                <PaginatorNumber page={1} scrollToRef={scrollToRef} />
                {currentPage > 6 && <Dots />}
              </>
            )}

            {pageNumbersBefore}

            <PaginatorNumber page={currentPage} scrollToRef={scrollToRef} isCurrent />

            {pageNumbersAfter}

            {currentPage <= totalPages - 5 && (
              <>
                {currentPage <= totalPages - 6 && <Dots />}
                <PaginatorNumber page={totalPages} scrollToRef={scrollToRef} />
              </>
            )}
          </ol>
        </li>

        <li className="pager__status text--sm" aria-hidden="true">
          <span className="pager__current">
            <em>{currentPage}</em>
            <span>/</span>
            {totalPages}
          </span>
        </li>

        <li className="pager__item pager__item--next">
          <PaginatorLink
            className="pager__next"
            page={currentPage + 1}
            scrollToRef={scrollToRef}
            isDisabled={currentPage >= totalPages}
          >
            <NextLabel />
            <SVGIcon id='icon-arrow-right' size={20} className='pager__icon' />
          </PaginatorLink>
        </li>
      </ul>
    </nav>
  )
}

export const pageSearchParam = 'page'

const PaginatorNumber = ({
  page,
  scrollToRef,
  isCurrent,
}: {
  page: number
  scrollToRef?: RefObject<HTMLElement>
  isCurrent?: boolean
}) => (
  <li className="pager__page">
    <PaginatorLink
      className={`pager__action${isCurrent ? ' pager__action--active' : ''}`}
      page={page}
      scrollToRef={scrollToRef}
      isDisabled={false}
      isCurrent={isCurrent}
    >
      <PageNumberLabel page={page} />
    </PaginatorLink>
  </li>
)

const PaginatorLink = ({
  className,
  page,
  scrollToRef,
  children,
  isDisabled,
  isCurrent,
}: {
  className: string
  page: number
  scrollToRef?: RefObject<HTMLElement>
  children: ReactNode
  isDisabled: boolean
  isCurrent?: boolean
}) => {
  const current = `Page ${page} (current page)`
  const notCurrent = `Go to page ${page}`
  return (
    <Link
      className={className}
      to={getPathWithUpdatedSearchParams((searchParams) => {
        if (isDisabled) return
        if (page > 1) searchParams.set(pageSearchParam, page.toString())
        else searchParams.delete(pageSearchParam)
      })}
      onClick={() => {
        if (scrollToRef?.current) scrollToRef.current.scrollIntoView()
      }}
      aria-current={isCurrent ? 'page' : undefined}
      aria-disabled={isDisabled || undefined}
      aria-label={isCurrent ? current : notCurrent}
      title={isCurrent ? current : notCurrent}
      tabIndex={isDisabled ? -1 : undefined}
    >
      {children}
    </Link>
  )
}

const PageNumberLabel = ({ page }: { page: number }) => <span className="pager__number">{page}</span>

const Dots = () => (
  <li className="pager__page pager__page--dots" aria-hidden="true">
    <span className="pager__dots">...</span>
  </li>
)

const PreviousLabel = () => <span className='pager__label'>{t<string>('Pager:previous')}</span>
const NextLabel = () => <span className='pager__label'>{t<string>('Pager:next')}</span>
